import React, { useCallback, useEffect, useState } from 'react';
import { courseStagesService, CourseStage } from '../../../../services/courseStagesService';

// ---------------------------------------------------------------------------
// Prompt List
// ---------------------------------------------------------------------------

function PromptList({ onSelect }: { onSelect: (stage: CourseStage) => void }) {
  const [stages, setStages] = useState<CourseStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await courseStagesService.list();
        const sorted = (resp.stages || []).sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
        setStages(sorted);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load stages');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }
  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>;
  }

  return (
    <div className="bg-white border rounded overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead>
          <tr style={{ background: 'linear-gradient(135deg, #1a2332 0%, #2d3748 100%)' }}>
            <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm">Stage</th>
            <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm w-40">Model</th>
            <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm w-28">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {stages.map((stage) => {
            const hasPrompt = !!stage.prompt_template;
            return (
              <tr
                key={stage.stage_id}
                className={`transition-all ${hasPrompt ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                style={{ boxShadow: 'inset 0 0 0 0 #01bcd4' }}
                onMouseEnter={(e) => { if (hasPrompt) e.currentTarget.style.boxShadow = 'inset 4px 0 0 0 #01bcd4'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'inset 0 0 0 0 #01bcd4'; }}
                onClick={() => { if (hasPrompt) onSelect(stage); }}
              >
                <td className="px-3 py-3">
                  <span className="font-mono text-xs text-gray-400 mr-2">{stage.order}</span>
                  <span className="font-medium text-gray-900">{stage.name}</span>
                  <span className="text-xs text-gray-400 ml-2">{stage.stage_id}</span>
                </td>
                <td className="px-3 py-3">
                  {hasPrompt ? (
                    <span className="text-gray-500">{stage.model || '-'}</span>
                  ) : (
                    <span className="text-gray-300 italic">(non-LLM)</span>
                  )}
                </td>
                <td className="px-3 py-3 text-gray-500">
                  {stage.updatedAt ? new Date(stage.updatedAt).toLocaleDateString() : '-'}
                </td>
              </tr>
            );
          })}
          {stages.length === 0 && (
            <tr><td className="px-3 py-4 text-gray-500" colSpan={3}>No stages found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Global Prompt Editor
// ---------------------------------------------------------------------------

function GlobalPromptEditor({ stage, onBack, onSaved }: {
  stage: CourseStage;
  onBack: () => void;
  onSaved: () => void;
}) {
  const [template, setTemplate] = useState(stage.prompt_template || '');
  const [systemPrompt, setSystemPrompt] = useState(stage.system_prompt || '');
  const [model, setModel] = useState(stage.model || '');
  const [temperature, setTemperature] = useState(stage.temperature ?? 0.2);
  const [maxTokens, setMaxTokens] = useState(stage.max_tokens ?? 4000);
  const [thinkingEnabled, setThinkingEnabled] = useState(!!stage.thinking && stage.thinking.type === 'enabled');
  const [thinkingBudget, setThinkingBudget] = useState(stage.thinking?.budget_tokens ?? 10000);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  const stageName = stage.name || stage.stage_id;

  const templateVars = Array.from(new Set(
    (template || '').match(/\{\{\s*[\w.]+\s*\}\}/g)?.map(v => v.replace(/[{}\s]/g, '')) || []
  ));

  const isDirty = template !== (stage.prompt_template || '') ||
    systemPrompt !== (stage.system_prompt || '') ||
    model !== (stage.model || '') ||
    (!thinkingEnabled && temperature !== (stage.temperature ?? 0.2)) ||
    maxTokens !== (stage.max_tokens ?? 4000) ||
    thinkingEnabled !== (!!stage.thinking && stage.thinking.type === 'enabled');

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const payload: Record<string, unknown> = {
        prompt_template: template,
        system_prompt: systemPrompt || null,
        model,
        max_tokens: maxTokens,
      };
      if (thinkingEnabled) {
        payload.thinking = { type: 'enabled', budget_tokens: thinkingBudget };
        payload.temperature = null;
      } else {
        payload.thinking = null;
        payload.temperature = temperature;
      }
      await courseStagesService.update(stage.stage_id, payload as any);
      setSavedMessage('Saved successfully');
      onSaved();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
      setShowConfirm(false);
    }
  }, [template, systemPrompt, model, temperature, maxTokens, thinkingEnabled, thinkingBudget, stage.stage_id, onSaved]);

  const handleReset = () => {
    setTemplate(stage.prompt_template || '');
    setSystemPrompt(stage.system_prompt || '');
    setModel(stage.model || '');
    setTemperature(stage.temperature ?? 0.2);
    setMaxTokens(stage.max_tokens ?? 4000);
    setThinkingEnabled(!!stage.thinking && stage.thinking.type === 'enabled');
    setThinkingBudget(stage.thinking?.budget_tokens ?? 10000);
    setError(null);
    setSavedMessage(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-3 inline-block">
          &larr; Back to prompts
        </button>
        <h2 className="text-lg font-semibold text-gray-900">{stageName}</h2>
        <p className="text-xs text-gray-400 mt-0.5">{stage.stage_id}</p>
      </div>

      <div className="bg-white border rounded p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Settings</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Model</label>
            <input type="text" value={model} onChange={(e) => setModel(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Max Tokens</label>
            <input type="number" step="100" min="100" value={maxTokens}
              onChange={(e) => setMaxTokens(parseInt(e.target.value) || 4000)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div />
        </div>
        <div className="grid grid-cols-3 gap-4 mt-3 items-end">
          <div>
            <label className="flex items-center gap-2 text-xs font-medium text-gray-600 cursor-pointer">
              <input type="checkbox" checked={thinkingEnabled} onChange={(e) => {
                setThinkingEnabled(e.target.checked);
                if (e.target.checked) setTemperature(0);
              }} className="rounded border-gray-300" />
              Extended Thinking
            </label>
          </div>
          {thinkingEnabled ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Thinking Budget (tokens)</label>
              <input type="number" step="1000" min="1000" value={thinkingBudget}
                onChange={(e) => setThinkingBudget(parseInt(e.target.value) || 10000)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Temperature</label>
              <input type="number" step="0.1" min="0" max="2" value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500" />
            </div>
          )}
          {thinkingEnabled && (
            <div className="text-xs text-gray-400 italic pb-2">Temperature is fixed at 1.0 when thinking is enabled</div>
          )}
        </div>
      </div>

      <div className="bg-white border rounded p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">System Prompt</h3>
        <textarea value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} rows={3}
          placeholder="Optional system prompt..."
          className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y" />
      </div>

      <div className="bg-white border rounded p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Prompt Template</h3>
        <textarea value={template} onChange={(e) => setTemplate(e.target.value)} rows={20}
          className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y" />
      </div>

      {templateVars.length > 0 && (
        <details className="bg-white border rounded p-4">
          <summary className="text-xs font-semibold text-gray-400 uppercase tracking-wider cursor-pointer">
            Template Variables ({templateVars.length})
          </summary>
          <div className="mt-3 space-y-1">
            {templateVars.map(v => (
              <div key={v} className="text-sm font-mono text-gray-600 py-0.5">
                {'{{ '}{v}{' }}'}
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="flex items-center gap-3">
        <button onClick={() => setShowConfirm(true)} disabled={!isDirty || saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={handleReset} disabled={!isDirty}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
          Discard changes
        </button>
        {savedMessage && <span className="text-sm text-green-600">{savedMessage}</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>Changes to this template affect all future courses. Per-course overrides (set within a course's stage panel) take precedence over these defaults.</span>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" onClick={() => setShowConfirm(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Save default template?</h3>
            <p className="text-sm text-gray-600">
              This will change the default prompt template for all future courses. Per-course overrides will not be affected.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button className="px-3 py-1.5 text-sm border rounded-md border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700" onClick={handleSave}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section root
// ---------------------------------------------------------------------------

export function CoursePromptManagement() {
  const [selectedStage, setSelectedStage] = useState<CourseStage | null>(null);
  const [listKey, setListKey] = useState(0);

  if (selectedStage) {
    return (
      <GlobalPromptEditor
        stage={selectedStage}
        onBack={() => setSelectedStage(null)}
        onSaved={() => setListKey(k => k + 1)}
      />
    );
  }

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-900 mb-6">Prompt Templates</h1>
      <p className="text-sm text-gray-500 mb-4">
        Edit the default Jinja2 prompt templates used by each pipeline stage. Per-course overrides (set within a course's stage panel) take precedence over these defaults.
      </p>
      <PromptList key={listKey} onSelect={setSelectedStage} />
    </div>
  );
}
